import React from 'react';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';

import { fetchActivityDetails } from '../../../api/activity.api';
import { useActor } from '../../../components/hooks/useActor';
import { RecentActivityPanel, type ActivityEvent } from './RecentActivityPanel';

const RECENT_EVENT_COUNT = 5;
const VIEW_LOG_PATH = '/customer/activity';

/** Data for the dashboard's recent-activity panel. */
export const ActivityCard = () => {
  const actor = useActor();

  const query = useQuery(
    // The activity service scopes by the caller rather than by a parameter, so
    // the account only appears in the cache key -- without it, a switch serves
    // the previous account's events from cache.
    ['dashboard-recent-activity', actor.account?.id],
    () =>
      fetchActivityDetails({ orderBy: 'modified', order: 'desc' }).then(
        ({ data }: any) => toActivityEvents(data)
      ),
    { refetchOnWindowFocus: false }
  );

  return (
    <RecentActivityPanel
      events={query.data ?? []}
      isLoading={query.isInitialLoading}
      isError={query.isError}
      viewLogHref={VIEW_LOG_PATH}
    />
  );
};

/**
 * `modified` is the activity service's event time. The card previously read a
 * `timestamp` field that the payload does not carry, so every row rendered the
 * current time instead of the event's.
 */
export const toActivityEvents = (data: unknown): ActivityEvent[] =>
  _.get(data, 'content', [])
    .slice(0, RECENT_EVENT_COUNT)
    .map((event: any, index: number) => ({
      id: event?.id ?? `activity-${index}`,
      eventSummary: event?.eventSummary ?? '',
      modified: event?.modified,
    }))
    .filter((event: ActivityEvent) => Boolean(event.eventSummary));

export default ActivityCard;
